import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { AuthCardTitle, AuthShell } from '../components/auth/AuthShell';
import { useAuth } from '../contexts/AuthContext';
import { getChatGPTConsentReturnTo, isAllowedChatGPTRedirect, isValidChatGPTRequestId } from '../lib/chatgptConsent';
import { hasAnalyticsConsent, recordChatGPTConsentCompleted } from '../lib/analytics/productEvents';

interface ConsentRequest {
  requestId: string;
  clientName: 'ChatGPT';
  scope: 'returncatalyst.read';
}

type RequestStatus = 'loading' | 'ready' | 'unavailable';

function isConsentRequest(value: unknown, requestId: string): value is ConsentRequest {
  if (!value || typeof value !== 'object') return false;
  const request = value as Record<string, unknown>;
  return request.requestId === requestId && request.clientName === 'ChatGPT' && request.scope === 'returncatalyst.read';
}

function requestUnavailableMessage(): string {
  return 'This connection request is unavailable. It may have expired. Return to ChatGPT and try again.';
}

function ConsentCard({ children }: { children: ReactNode }) {
  return (
    <AuthShell contentClassName="max-w-[34rem]">
      <Card className="w-full border-border bg-card shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
        {children}
      </Card>
    </AuthShell>
  );
}

interface ChatGPTConsentProps {
  onRedirect?: (redirectUrl: string) => void;
}

export function ChatGPTConsent({ onRedirect = (redirectUrl) => window.location.assign(redirectUrl) }: ChatGPTConsentProps) {
  const { user, userProfile, loading: authLoading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const requestId = searchParams.get('request');
  const [status, setStatus] = useState<RequestStatus>('loading');
  const [request, setRequest] = useState<ConsentRequest | null>(null);
  const [error, setError] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [switchAccountError, setSwitchAccountError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const sessionRef = useRef(0);

  const returnTo = useMemo(
    () => getChatGPTConsentReturnTo(`${location.pathname}${location.search}`),
    [location.pathname, location.search],
  );
  const signInPath = `/login?returnTo=${encodeURIComponent(returnTo)}`;

  const loadRequest = useCallback(async (isCurrent: () => boolean) => {
    if (!user || !isValidChatGPTRequestId(requestId)) return;

    setStatus('loading');
    setError('');
    setRequest(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`/api/chatgpt/oauth/request?requestId=${encodeURIComponent(requestId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('request_unavailable');

      const payload: unknown = await response.json();
      if (!isConsentRequest(payload, requestId)) throw new Error('invalid_request');
      if (!isCurrent()) return;

      setRequest(payload);
      setStatus('ready');
    } catch {
      if (!isCurrent()) return;
      setError(requestUnavailableMessage());
      setStatus('unavailable');
    }
  }, [requestId, user]);

  useEffect(() => {
    const session = ++sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    setRequest(null);
    setError('');
    setSubmissionError('');
    setSubmitting(false);
    setSwitchAccountError('');
    setSwitchingAccount(false);
    setStatus('loading');

    if (!authLoading && user && user.emailVerified && userProfile?.status === 'active') {
      void loadRequest(isCurrent);
    }

    return () => {
      if (sessionRef.current === session) sessionRef.current += 1;
    };
  }, [authLoading, loadRequest, retryCount, user, userProfile?.status]);

  const submitConsent = async (approved: boolean) => {
    if (!user || !request || submitting || switchingAccount) return;
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;

    setSubmitting(true);
    setSubmissionError('');
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/chatgpt/oauth/consent', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId: request.requestId, approved }),
      });
      if (!response.ok) throw new Error('consent_unavailable');

      const payload: unknown = await response.json();
      const redirectUrl = typeof (payload as { redirectUrl?: unknown })?.redirectUrl === 'string'
        ? (payload as { redirectUrl: string }).redirectUrl
        : '';
      if (!isAllowedChatGPTRedirect(redirectUrl)) {
        throw new Error('unsafe_redirect');
      }
      if (!isCurrent()) return;

      if (hasAnalyticsConsent(userProfile?.preferences)) {
        recordChatGPTConsentCompleted(approved ? 'approved' : 'denied');
      }
      onRedirect(redirectUrl);
    } catch {
      if (!isCurrent()) return;
      if (hasAnalyticsConsent(userProfile?.preferences)) {
        recordChatGPTConsentCompleted('failed');
      }
      setSubmissionError('We could not complete this connection safely. Please try again from ChatGPT.');
    } finally {
      if (isCurrent()) setSubmitting(false);
    }
  };

  const switchAccount = async () => {
    if (switchingAccount || submitting) return;
    setSwitchingAccount(true);
    setSwitchAccountError('');
    try {
      await logout();
      navigate(signInPath);
    } catch {
      setSwitchAccountError('We could not sign out of this account. Please try again.');
      setSwitchingAccount(false);
    }
  };

  if (authLoading) {
    return (
      <ConsentCard>
        <CardContent className="flex min-h-[14rem] items-center justify-center">
          <p className="text-sm text-muted-foreground" role="status">Loading connection request…</p>
        </CardContent>
      </ConsentCard>
    );
  }

  if (!isValidChatGPTRequestId(requestId)) {
    return (
      <ConsentCard>
        <CardHeader>
          <AuthCardTitle>Connection request unavailable</AuthCardTitle>
          <CardDescription>Return to ChatGPT and start the connection again.</CardDescription>
        </CardHeader>
      </ConsentCard>
    );
  }

  if (!user) {
    return (
      <ConsentCard>
        <CardHeader className="space-y-3">
          <AuthCardTitle>Connect ChatGPT to ReturnCatalyst</AuthCardTitle>
          <CardDescription className="text-base">Sign in to review and approve this connection request.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="h-11 w-full"><Link to={signInPath}>Sign in to continue</Link></Button>
        </CardContent>
      </ConsentCard>
    );
  }

  if (!user.emailVerified) {
    return (
      <ConsentCard>
        <CardHeader className="space-y-3">
          <AuthCardTitle>Verify your email to continue</AuthCardTitle>
          <CardDescription className="text-base">Verify the email for this ReturnCatalyst account before connecting ChatGPT.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SignedInEmail email={user.email} />
          <Button asChild className="h-11 w-full"><Link to={`/verify-email?returnTo=${encodeURIComponent(returnTo)}`}>Verify email</Link></Button>
          <AccountSwitchAction loading={switchingAccount} error={switchAccountError} onSwitch={() => void switchAccount()} />
        </CardContent>
      </ConsentCard>
    );
  }

  if (!userProfile || userProfile.status !== 'active') {
    return (
      <ConsentCard>
        <CardHeader className="space-y-3">
          <AuthCardTitle>Account access is unavailable</AuthCardTitle>
          <CardDescription className="text-base">This ReturnCatalyst account must be active before it can connect ChatGPT.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SignedInEmail email={user.email} />
          <p className="text-sm text-muted-foreground">If you have another active account, sign in with that account and return here to continue.</p>
          <AccountSwitchAction loading={switchingAccount} error={switchAccountError} onSwitch={() => void switchAccount()} />
        </CardContent>
      </ConsentCard>
    );
  }

  return (
    <ConsentCard>
      <CardHeader className="space-y-3">
        <AuthCardTitle>Connect ChatGPT to ReturnCatalyst</AuthCardTitle>
        <CardDescription className="text-base">Review the access before you continue.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {status !== 'ready' && <SignedInEmail email={user.email} />}

        {status === 'loading' && <p className="text-sm text-muted-foreground" role="status">Loading connection request…</p>}

        {status === 'unavailable' && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {status === 'ready' && request && (
          <>
            <ChatGPTConsentApproval email={user.email} clientName={request.clientName} submitting={submitting} disabled={switchingAccount} error={submissionError} onApprove={() => void submitConsent(true)} onDeny={() => void submitConsent(false)} />
            <AccountSwitchAction loading={switchingAccount} disabled={submitting} error={switchAccountError} onSwitch={() => void switchAccount()} />
          </>
        )}

        {status === 'unavailable' && (
          <Button type="button" variant="outline" className="h-11 w-full" onClick={() => setRetryCount((count) => count + 1)}>
            Retry
          </Button>
        )}
      </CardContent>
    </ConsentCard>
  );
}

function SignedInEmail({ email }: { email: string | null | undefined }) {
  return <p className="text-sm text-muted-foreground">Signed in as <span className="font-medium text-foreground">{email || 'your ReturnCatalyst account'}</span></p>;
}

interface ChatGPTConsentApprovalProps {
  email: string | null | undefined;
  clientName: 'ChatGPT';
  submitting: boolean;
  disabled?: boolean;
  error: string;
  onApprove: () => void;
  onDeny: () => void;
}

export function ChatGPTConsentApproval({ email, clientName, submitting, disabled = false, error, onApprove, onDeny }: ChatGPTConsentApprovalProps) {
  return (
    <>
      <SignedInEmail email={email} />
      <div className="space-y-3 border-y border-border py-5">
        <p className="text-sm font-medium text-foreground">{clientName} is requesting read-only access.</p>
        <p className="text-sm leading-6 text-muted-foreground">ChatGPT will be able to read the deals and documents available to this ReturnCatalyst account. It cannot modify your deals or documents.</p>
        <p className="text-sm leading-6 text-muted-foreground">You can disconnect or revoke access in ChatGPT at any time.</p>
        <p className="text-sm leading-6 text-muted-foreground">Disconnecting ReturnCatalyst does not delete data already returned in ChatGPT conversations. <Link className="font-medium text-primary hover:underline" to="/privacy">Review our privacy policy</Link>.</p>
      </div>

      {error && <Alert variant="destructive" role="alert"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="grid gap-3 sm:grid-cols-2">
        <Button type="button" className="h-11" onClick={onApprove} disabled={submitting || disabled}>{submitting ? 'Connecting…' : 'Allow access'}</Button>
        <Button type="button" variant="outline" className="h-11" onClick={onDeny} disabled={submitting || disabled}>Cancel</Button>
      </div>
    </>
  );
}

function AccountSwitchAction({ loading, disabled = false, error, onSwitch }: { loading: boolean; disabled?: boolean; error: string; onSwitch: () => void }) {
  return (
    <div className="space-y-2">
      {error && <Alert variant="destructive" role="alert"><AlertDescription>{error}</AlertDescription></Alert>}
      <Button type="button" variant="link" className="h-auto p-0 text-sm font-medium" onClick={onSwitch} disabled={loading || disabled}>
        {loading ? 'Signing out…' : 'Sign in with another account'}
      </Button>
    </div>
  );
}
